import { useEffect, useState } from "react";
import { ExternalLink, ImagePlus } from "lucide-react";

const normalizeDisplayImageUrl = (value = "") => {
  let trimmed = String(value || "").trim();
  const markdownMatch = trimmed.match(/!?\[[^\]]*\]\(([^)]+)\)/);
  if (markdownMatch) trimmed = markdownMatch[1];
  const srcMatch = trimmed.match(/src=["']([^"']+)["']/i);
  if (srcMatch) trimmed = srcMatch[1];
  trimmed = trimmed.replace(/^<|>$/g, "").replace(/^["']|["']$/g, "").trim();
  if (!trimmed) return "";
  const googleDriveFileMatch = trimmed.match(/drive\.google\.com\/file\/d\/([^/]+)/i);
  const googleDriveIdMatch = trimmed.includes("drive.google.com") ? trimmed.match(/[?&]id=([^&]+)/i) : null;
  const googleDriveId = googleDriveFileMatch?.[1] || googleDriveIdMatch?.[1];
  if (googleDriveId) return `https://drive.google.com/uc?export=view&id=${encodeURIComponent(googleDriveId)}`;
  if (/dropbox\.com/i.test(trimmed)) {
    const dropboxUrl = trimmed.replace("www.dropbox.com", "dl.dropboxusercontent.com").replace("dropbox.com", "dl.dropboxusercontent.com").replace(/[?&]dl=0/i, "").replace(/[?&]raw=0/i, "");
    return encodeURI(`${dropboxUrl}${dropboxUrl.includes("?") ? "&raw=1" : "?raw=1"}`);
  }
  if (/^(https?:|data:image\/|blob:)/i.test(trimmed)) return encodeURI(trimmed);
  if (trimmed.startsWith("//")) return `https:${encodeURI(trimmed)}`;
  if (/^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(trimmed)) return `https://${encodeURI(trimmed)}`;
  return trimmed;
};

function ClientLogoCard({ logo, index }) {
  const imageUrl = normalizeDisplayImageUrl(logo.image);
  const logoName = logo.name || `Client ${index + 1}`;
  const [imageStatus, setImageStatus] = useState(imageUrl ? "loading" : "empty");

  useEffect(() => {
    setImageStatus(imageUrl ? "loading" : "empty");
    if (!imageUrl) return undefined;
    const fallbackTimer = window.setTimeout(() => {
      setImageStatus((currentStatus) => (currentStatus === "loading" ? "error" : currentStatus));
    }, 1400);
    return () => window.clearTimeout(fallbackTimer);
  }, [imageUrl]);

  return (
    <div className="client-logo-card" data-testid={`client-logo-card-${index + 1}`}>
      {imageUrl && imageStatus !== "error" ? (
        <img src={imageUrl} alt={logoName} data-testid={`client-logo-image-${index + 1}`} onLoad={() => setImageStatus("loaded")} onError={() => setImageStatus("error")} />
      ) : (
        <span data-testid={`client-logo-placeholder-${index + 1}`}>{logoName}</span>
      )}
    </div>
  );
}

export function ProofAreas({ content, images = [], logos = [] }) {
  const visibleLogos = logos.slice(0, 10).filter((logo) => normalizeDisplayImageUrl(logo.image));

  return (
    <section id="projects" className="section proof-section" data-slide="04" data-testid="proof-section">
      <div className="section-heading wide">
        <p className="overline" data-testid="proof-overline">{content.overline}</p>
        <h2 data-testid="proof-title">{content.title}</h2>
        <p data-testid="proof-description">{content.description}</p>
      </div>

      {content.stats && (
        <div className="proof-stat-strip" data-testid="proof-stat-strip">
          {content.stats.map((stat, index) => (
            <div className="proof-stat" key={stat.value} data-testid={`proof-stat-${index + 1}`}>
              <strong data-testid={`proof-stat-value-${index + 1}`}>{stat.value}</strong>
              <span data-testid={`proof-stat-label-${index + 1}`}>{stat.label}</span>
            </div>
          ))}
        </div>
      )}

      <div className="proof-grid" data-testid="proof-card-grid">
        {content.cards.map((card, index) => (
          <article className={`proof-card ${card.featured ? "featured-proof" : ""}`} key={card.title} data-testid={`proof-card-${index + 1}`}>
            <div className="proof-image-slot" data-testid={`proof-image-slot-${index + 1}`}>
              {images[index]?.image ? (
                <img src={images[index].image} alt={card.title} data-testid={`proof-image-${index + 1}`} />
              ) : (
                <>
                  <ImagePlus size={28} aria-hidden="true" />
                  <span>{card.category}</span>
                </>
              )}
            </div>
            <div className="proof-body">
              <p className="card-eyebrow" data-testid={`proof-card-category-${index + 1}`}>{card.category}</p>
              <h3 data-testid={`proof-card-title-${index + 1}`}>{card.title}</h3>
              <ul data-testid={`proof-card-scope-${index + 1}`}>
                {card.scope.map((scope) => (
                  <li key={scope} data-testid={`proof-card-scope-item-${index + 1}-${scope.toLowerCase().replaceAll(" ", "-").replaceAll("/", "")}`}>{scope}</li>
                ))}
              </ul>
              <p className="proof-outcome" data-testid={`proof-card-outcome-${index + 1}`}>{card.outcome}</p>
              {card.link && card.publicLinkEnabled !== false && (
                <a
                  className="proof-card-link"
                  href={card.link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`proof-card-link-${index + 1}`}
                >
                  <span>{card.linkLabel || "Learn more"}</span>
                  <ExternalLink size={16} aria-hidden="true" />
                </a>
              )}
            </div>
          </article>
        ))}
      </div>

      {visibleLogos.length > 0 && (
        <div className="client-logo-strip" data-testid="client-logo-strip">
          <div className="client-logo-heading" data-testid="client-logo-heading">
            <p className="overline" data-testid="client-logo-eyebrow">{content.logoStrip?.eyebrow || "Client References"}</p>
            <h3 data-testid="client-logo-title">{content.logoStrip?.title || "Client logo placeholders"}</h3>
          </div>
          <div className="client-logo-scroller" data-testid="client-logo-scroller" aria-label={content.logoStrip?.title || "Client logos"}>
            {visibleLogos.map((logo, index) => (
              <ClientLogoCard logo={logo} index={index} key={`${logo.name}-${index}`} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}